<script setup>
definePage({
  name: 'shipment-warehouse-invoices-view',
  meta: {
    requiresAuth: true,
    // money clerks need to read the invoice to verify the amount
    // before marking the group paid (they cannot mark paid from
    // here — the action lives on the container page — but they
    // navigate here from the invoice list to confirm details).
    role: ['admin', 'manager', 'user', 'money'],
  },
})
import { ref, watch, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import invoiceLogo from '@images/svg/godana.svg'
import { useInvoiceCancellation } from '@/composables/shipment/useInvoiceCancellation'
import CancelInvoiceDialog from '@/components/shipment/CancelInvoiceDialog.vue'
import { useUserRoles } from '@/composables/useUserRoles'
import { formatCurrency } from '@/utils/currencyUtils'

const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()

const invoiceId = computed(() => route.params.invoiceId)
const invoice = ref(null)
const isLoading = ref(false)
const exchangeRates = ref({ usd_to_mga: 4750, cny_to_mga: 650 })
const isSavingCurrency = ref(false)

const currencyOptions = ['MGA', 'USD', 'CNY']

const currentCurrency = computed(() => (invoice.value?.currency || 'MGA').toUpperCase())

// Stored amounts on shipping_transactions are MGA. The line.shipping_price/rate_used
// were computed in MGA at quote time. To display in USD/CNY we divide by the
// appropriate active exchange rate.
const conversionFactor = computed(() => {
  switch (currentCurrency.value) {
    case 'USD': return 1 / (exchangeRates.value.usd_to_mga || 1)
    case 'CNY': return 1 / (exchangeRates.value.cny_to_mga || 1)
    default: return 1
  }
})

const currencySymbol = computed(() => {
  switch (currentCurrency.value) {
    case 'USD': return '$'
    case 'CNY': return '¥'
    default: return 'Ar'
  }
})

const formatMoney = amountMga => {
  const number = Number(amountMga)
  if (!Number.isFinite(number)) return '0'
  const converted = number * conversionFactor.value
  return formatCurrency(converted, currentCurrency.value)
}

// Fetch invoice details
const fetchInvoice = async () => {
  isLoading.value = true
  try {
    const [invoiceResp, ratesResp] = await Promise.all([
      axios.get(`shipment/warehouse/invoices/${invoiceId.value}`),
      axios.get('shipment/settings/exchange-rates'),
    ])
    invoice.value = invoiceResp.data.data
    exchangeRates.value = ratesResp.data
  } catch (error) {
    console.error('Error fetching invoice:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong')
    })
  } finally {
    isLoading.value = false
  }
}

const updateCurrency = async (newCurrency) => {
  if (!invoice.value) return
  isSavingCurrency.value = true
  try {
    await axios.put(`shipment/warehouse/invoices/${invoice.value.id}/currency`, {
      currency: newCurrency,
    })
    invoice.value.currency = newCurrency
  } catch (error) {
    console.error(error)
    showSnackbar({ color: 'error', text: t('messages.something_went_wrong') })
  } finally {
    isSavingCurrency.value = false
  }
}

// Download the invoice as PDF. We hit the backend dompdf endpoint instead
// of rendering client-side via html2pdf so Vue + Mobile share the exact
// same Blade-rendered output (single source of truth for layout). The
// previous html2pdf path drifted from the mobile rendering and was hard
// to keep in sync.
const downloadPdf = async () => {
  if (!invoice.value) return
  try {
    const response = await axios.get(
      `shipment/warehouse/invoices/${invoice.value.id}/pdf`,
      { responseType: 'blob' },
    )
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `invoice-${invoiceDisplayNumber.value}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading invoice PDF:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  }
}

// Navigate back to client groups
const goBack = () => {
  router.push({
    name: 'shipment-warehouse-client-groups',
    params: { containerId: invoice.value.client_group.container_arrival_id }
  })
}

// Cancel-invoice flow (superadmin only). On success, navigate back
// to the container's client groups page — the cancelled invoice no
// longer has a detail to show, and the group is now moveable.
const invoiceCancellation = useInvoiceCancellation({
  onSuccess: async () => {
    if (invoice.value?.client_group?.container_arrival_id) {
      router.push({
        name: 'shipment-warehouse-client-groups',
        params: { containerId: invoice.value.client_group.container_arrival_id },
      })
    } else {
      router.push({ name: 'shipment-warehouse-invoices' })
    }
  },
})

const onCancelClicked = () => {
  if (!invoice.value) return
  const cust = invoice.value.client_group?.customer
  invoiceCancellation.request({
    invoiceId: invoice.value.id,
    groupId:   invoice.value.client_group?.id,
    groupRef:  cust?.name
      ? `${cust.name} (${invoice.value.quote_number})`
      : invoice.value.quote_number,
    externalNumber: invoice.value.external_invoice_number,
  })
}

// Mark-as-paid (money / admin / superadmin) — same dialog as the
// invoice list, but pre-filled from the open invoice.
const { isMoney, isSuperadmin: roleIsSuperadmin, isAdmin } = useUserRoles()
const canMarkPaid = computed(() =>
  invoice.value
  && invoice.value.status !== 'paid'
  && (isMoney.value || roleIsSuperadmin.value || isAdmin.value)
)

const paidDialog = ref(false)
const paidExternal = ref('')
const paidLoading = ref(false)

const onMarkPaidClicked = () => {
  paidExternal.value = invoice.value?.external_invoice_number || ''
  paidDialog.value = true
}

const closePaidDialog = () => {
  paidDialog.value = false
  paidExternal.value = ''
}

const confirmMarkPaid = async () => {
  const num = paidExternal.value?.trim()
  if (!num) {
    showSnackbar({
      color: 'warning',
      text: t('shipment.payer_requires_external_invoice_number'),
    })
    return
  }
  const groupId = invoice.value?.client_group?.id
  if (!groupId) {
    showSnackbar({ color: 'error', text: 'No group attached to this invoice' })
    return
  }
  paidLoading.value = true
  try {
    await axios.put(
      `shipment/warehouse/groups/${groupId}/workflow-status`,
      { workflow_status: 'PAYER', external_invoice_number: num },
    )
    showSnackbar({
      color: 'success',
      text: t('shipment.workflow_updated') || 'Statut mis à jour',
    })
    closePaidDialog()
    await fetchInvoice()  // reload so the status flips on screen
  } catch (e) {
    console.error('markPaid', e)
    showSnackbar({
      color: 'error',
      text: e.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    paidLoading.value = false
  }
}

// Format date as DD/MM/YYYY (matches existing official invoice template).
const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatFixed = (value, decimals) => {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return (0).toFixed(decimals)
  }
  return number.toFixed(decimals)
}

// Aggregate invoice lines by pricing category + calculation type so the
// printed invoice shows ONE summary row per fret-type (matching the
// FFI-2510-519 reference layout) instead of one row per parcel.
// Transport detection: container_arrival's transport_type wins (warehouse
// flow); partner shipments don't have a container so fall back to the
// parcel's own transport flag (0=air, 1=sea per IncomingParcel).
const resolvedTransportType = computed(() => {
  const fromContainer = invoice.value?.client_group?.container_arrival?.transport_type
  if (fromContainer) return fromContainer
  const sample = invoice.value?.lines?.[0]?.arrival_inspection?.incoming_parcel?.transport
  if (sample === 0) return 'air'
  if (sample === 1) return 'sea'
  return null
})

const aggregatedLines = computed(() => {
  if (!invoice.value?.lines?.length) return []
  const groups = {}
  for (const line of invoice.value.lines) {
    const cat = (line.pricing_category || 'TRANSPORT').toUpperCase()
    const calc = line.calculation_type || (line.volume > 0 ? 'volume' : 'weight')
    const transport = resolvedTransportType.value === 'air' ? 'AERIEN' : 'MARITIME'
    const subtype = transport === 'AERIEN' ? 'AVION' : (cat === 'MARQUE' ? 'BATEAU MARQUE' : 'BATEAU NORMAL')
    const key = `${cat}|${calc}|${subtype}`
    if (!groups[key]) {
      groups[key] = {
        fret: `FRET ${transport}`,
        type: subtype,
        unit: calc === 'volume' ? 'M3' : 'KG',
        rate: Number(line.rate_used) || 0,
        quantity: 0,
        amount: 0,
      }
    }
    groups[key].quantity += calc === 'volume'
      ? (Number(line.volume) || 0)
      : (Number(line.chargeable_weight) || Number(line.weight) || 0)
    groups[key].amount += Number(line.shipping_price) || 0
  }
  return Object.values(groups)
})

// French number-to-words (positive integers only — sufficient for invoice totals).
const numberToFrenchWords = (n) => {
  n = Math.abs(Math.round(Number(n) || 0))
  if (n === 0) return 'ZÉRO'

  const units = ['', 'UN', 'DEUX', 'TROIS', 'QUATRE', 'CINQ', 'SIX', 'SEPT', 'HUIT', 'NEUF',
    'DIX', 'ONZE', 'DOUZE', 'TREIZE', 'QUATORZE', 'QUINZE', 'SEIZE', 'DIX-SEPT', 'DIX-HUIT', 'DIX-NEUF']
  const tens = ['', '', 'VINGT', 'TRENTE', 'QUARANTE', 'CINQUANTE', 'SOIXANTE', 'SOIXANTE', 'QUATRE-VINGT', 'QUATRE-VINGT']

  const sayUnder1000 = (num) => {
    if (num === 0) return ''
    let result = ''
    const hundreds = Math.floor(num / 100)
    const rest = num % 100
    if (hundreds > 1) result += units[hundreds] + ' CENT' + (rest === 0 ? 'S' : '') + ' '
    else if (hundreds === 1) result += 'CENT '
    if (rest < 20) {
      result += units[rest]
    } else {
      const t = Math.floor(rest / 10)
      const u = rest % 10
      if (t === 7 || t === 9) {
        result += tens[t] + (u === 1 && t !== 7 ? '-ET-' : '-') + units[10 + u]
      } else {
        result += tens[t]
        if (t === 8 && u === 0) result += 'S'
        if (u > 0) result += (u === 1 && t !== 8 ? ' ET ' : '-') + units[u]
      }
    }
    return result.trim()
  }

  let parts = []
  const billions = Math.floor(n / 1_000_000_000)
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000)
  const thousands = Math.floor((n % 1_000_000) / 1_000)
  const rest = n % 1_000

  if (billions > 0) parts.push((billions === 1 ? 'UN' : sayUnder1000(billions)) + ' MILLIARD' + (billions > 1 ? 'S' : ''))
  if (millions > 0) parts.push((millions === 1 ? 'UN' : sayUnder1000(millions)) + ' MILLION' + (millions > 1 ? 'S' : ''))
  if (thousands > 0) parts.push((thousands === 1 ? '' : sayUnder1000(thousands) + ' ') + 'MILLE')
  if (rest > 0) parts.push(sayUnder1000(rest))

  return parts.join(' ').trim()
}

const totalInWords = computed(() => {
  if (!invoice.value) return ''
  const amount = Number(invoice.value.total_amount) * conversionFactor.value
  const currencyName = currentCurrency.value === 'MGA'
    ? 'ARIARY'
    : (currentCurrency.value === 'USD' ? 'DOLLARS' : 'YUAN')
  return numberToFrenchWords(amount) + ' ' + currencyName
})

const transportLabel = computed(() => {
  return resolvedTransportType.value === 'air' ? 'AVION' : 'BATEAU'
})

const parcelsCount = computed(() => invoice.value?.lines?.length || 0)

// Shipment reference code (e.g. "1.10_SEA") — falls back through the chain.
// Container number is intentionally NOT a fallback; we never leak it on
// customer-facing invoices.
const shipmentReference = computed(() => {
  const cg = invoice.value?.client_group
  return cg?.reference_code
    || cg?.container_arrival?.packing_list?.reference_code
    || ''
})

// Display number: prefer external invoice number (FFI-...) if reconciled,
// otherwise fall back to the system-generated quote number (GDN-Q-...).
const invoiceDisplayNumber = computed(() =>
  invoice.value?.external_invoice_number || invoice.value?.quote_number || ''
)

onMounted(() => {
  fetchInvoice()
})

// Refetch when `:invoiceId` changes — same fix as on the parcel/customer/
// container detail pages. Cmd+K to another invoice would otherwise leave
// the page rendering the previous invoice's data.
watch(invoiceId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    invoice.value = null
    fetchInvoice()
  }
})
</script>

<template>
  <div>
    <VProgressLinear v-if="isLoading" indeterminate />
    
    <div v-else-if="invoice">
      <VCard class="mb-4">
        <VCardText>
          <div class="d-flex align-center justify-space-between">
            <h2 class="text-h4">
              {{ t('shipment.invoice') }} #{{ invoiceDisplayNumber }}
            </h2>
            <div class="d-flex align-center ga-2">
              <VBtn
                icon
                variant="text"
                @click="goBack"
              >
                <VIcon icon="tabler-arrow-left" />
              </VBtn>
              <VSelect
                :model-value="currentCurrency"
                :items="currencyOptions"
                :label="t('shipment.currency')"
                density="compact"
                hide-details
                style="max-width: 140px"
                :disabled="isSavingCurrency"
                @update:model-value="updateCurrency"
              />
              <VBtn
                color="primary"
                class="ml-2"
                prepend-icon="tabler-download"
                @click="downloadPdf"
              >
                {{ t('shipment.download_pdf') }}
              </VBtn>
              <VBtn
                v-if="canMarkPaid"
                color="success"
                variant="elevated"
                class="ml-2"
                prepend-icon="tabler-cash-banknote"
                @click="onMarkPaidClicked"
              >
                {{ t('shipment.mark_as_paid_dialog_title') }}
              </VBtn>
              <VBtn
                v-if="invoiceCancellation.isSuperadmin"
                color="error"
                variant="tonal"
                class="ml-2"
                prepend-icon="tabler-receipt-off"
                @click="onCancelClicked"
              >
                {{ t('shipment.cancel_invoice') }}
              </VBtn>
            </div>
          </div>
        </VCardText>
      </VCard>

      <CancelInvoiceDialog :ctl="invoiceCancellation" />

      <!-- Mark-as-paid dialog -->
      <VDialog v-model="paidDialog" max-width="540" persistent>
        <VCard>
          <VCardTitle class="d-flex align-center">
            <VIcon icon="tabler-cash-banknote" color="success" class="me-2" />
            {{ t('shipment.mark_as_paid_dialog_title') }}
          </VCardTitle>
          <VCardText>
            <p class="mb-3">
              {{ t('shipment.mark_as_paid_dialog_body') }}
            </p>
            <VTextField
              v-model="paidExternal"
              :label="t('shipment.external_invoice_number_label')"
              :hint="t('shipment.external_invoice_number_hint')"
              persistent-hint
              variant="outlined"
              density="compact"
              autofocus
              @keyup.enter="confirmMarkPaid"
            />
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn variant="text" :disabled="paidLoading" @click="closePaidDialog">
              {{ t('common.cancel') }}
            </VBtn>
            <VBtn
              color="success"
              variant="elevated"
              prepend-icon="tabler-cash-banknote"
              :loading="paidLoading"
              :disabled="!paidExternal?.trim()"
              @click="confirmMarkPaid"
            >
              {{ t('shipment.mark_as_paid_dialog_title') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
      
      <VCard id="invoice-content">
        <VCardText class="invoice-sheet">
          <!-- Header: 3-column grid (logo+info | FACTURE+meta | optional stamp) -->
          <div class="invoice-header">
            <div class="invoice-header-left">
              <div class="invoice-logo-row">
                <component
                  :is="invoiceLogo"
                  class="invoice-logo"
                  role="img"
                  aria-label="Godana Service"
                />
                <div class="invoice-logo-text">
                  <div>GODANA</div>
                  <div>SERVICE</div>
                </div>
              </div>
              <p class="invoice-line invoice-company">GODANA SERVICE</p>
              <p class="invoice-line">Tsaralalàna, Antananarivo, Madagascar</p>
              <p class="invoice-line">contact@godana.mg</p>
              <p class="invoice-line">TEL : 034 10 970 02 - 034 49 929 06</p>
              <p class="invoice-line">NIF : 5004082771</p>
              <p class="invoice-line">STAT : 46101 11 2020 0 10637</p>
            </div>
            <div class="invoice-header-center">
              <div class="invoice-title">FACTURE</div>
              <div class="invoice-meta">
                <div class="invoice-meta-row">
                  <span class="invoice-meta-label">Date:</span>
                  <span class="invoice-meta-value">{{ formatDate(invoice.created_at) }}</span>
                </div>
                <div class="invoice-meta-row">
                  <span class="invoice-meta-label">N&#176;:</span>
                  <span class="invoice-meta-value">{{ invoiceDisplayNumber }}</span>
                </div>
              </div>
            </div>
            <div class="invoice-header-right" />
          </div>

          <!-- Customer block: 'DOIT:' label + name + optional phone.
               Matches the convention of formal Malagasy invoicing where the
               debtor (= the one who owes) is introduced with 'DOIT:'. -->
          <div class="invoice-customer">
            <div class="invoice-customer-doit">DOIT :</div>
            <div class="invoice-customer-name">{{ invoice.client_group?.customer?.name || '—' }}</div>
            <div
              v-if="invoice.client_group?.customer?.phone"
              class="invoice-customer-phone"
            >
              {{ invoice.client_group.customer.phone }}
            </div>
          </div>

          <!-- Aggregated fret table: ONE row per pricing-category/calculation -->
          <table class="fret-table">
            <thead>
              <tr>
                <th class="col-num">N°</th>
                <th class="col-fret">FRET</th>
                <th class="col-type">TYPE</th>
                <th class="col-unit">UNITE</th>
                <th class="col-pu">P.U</th>
                <th class="col-qty">QUANTITE</th>
                <th class="col-amount">MONTANT</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in aggregatedLines" :key="index">
                <td class="col-num">{{ index + 1 }}</td>
                <td class="col-fret">{{ row.fret }}</td>
                <td class="col-type">{{ row.type }}</td>
                <td class="col-unit">{{ row.unit }}</td>
                <td class="col-pu">{{ formatMoney(row.rate) }}</td>
                <td class="col-qty">{{ formatFixed(row.quantity, 6) }}</td>
                <td class="col-amount">{{ formatMoney(row.amount) }}</td>
              </tr>
              <tr class="total-row">
                <td colspan="5" />
                <td class="col-qty"><strong>TOTAL</strong></td>
                <td class="col-amount"><strong>{{ formatMoney(invoice.total_amount) }}</strong></td>
              </tr>
            </tbody>
          </table>

          <!-- Total in words (French) -->
          <p class="invoice-words">
            Arrêtée la présente facture à <strong>{{ totalInWords }}</strong>.
          </p>

          <!-- Per-parcel breakdown (DETAIL PAR COLIS) — kept for transparency,
               so the customer can see exactly which barcodes they're paying for.
               Aggregated table above gives the totals; this gives the audit trail. -->
          <div class="detail-section">
            <h4 class="detail-title">DÉTAIL PAR COLIS</h4>
            <table class="detail-table">
              <thead>
                <tr>
                  <th class="d-num">N°</th>
                  <th class="d-bag">SAC</th>
                  <th class="d-tracking">TRACKING</th>
                  <th class="d-dim">DIMENSIONS (cm)</th>
                  <th class="d-weight">POIDS</th>
                  <th class="d-volume">VOLUME (m³)</th>
                  <th class="d-amount">MONTANT</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, index) in invoice.lines" :key="index">
                  <td class="d-num">{{ index + 1 }}</td>
                  <td class="d-bag">{{ line.arrival_inspection?.incoming_parcel?.bag_code || '—' }}</td>
                  <td class="d-tracking">{{ line.arrival_inspection?.incoming_parcel?.barcode || '—' }}</td>
                  <td class="d-dim">{{ line.length }} × {{ line.width }} × {{ line.height }}</td>
                  <td class="d-weight">{{ line.weight }} kg</td>
                  <td class="d-volume">{{ formatFixed(line.volume, 4) }}</td>
                  <td class="d-amount">{{ formatMoney(line.shipping_price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Note + parcel count -->
          <div class="invoice-note">
            <div class="invoice-note-row">
              <span class="invoice-note-label">NOTE :</span>
              <span class="invoice-note-value">
                ARRIVAGE PAR {{ transportLabel }}<template v-if="shipmentReference"> — {{ shipmentReference }}</template>
              </span>
            </div>
            <div class="invoice-note-row">
              <span class="invoice-note-label" />
              <span class="invoice-note-value">{{ parcelsCount }} colis</span>
            </div>
          </div>

          <!-- Signatures -->
          <div class="invoice-signatures">
            <div class="invoice-signature-block">
              <div class="invoice-signature-label">LE RESPONSABLE</div>
            </div>
            <div class="invoice-signature-block">
              <div class="invoice-signature-label">LE CLIENT</div>
            </div>
          </div>
        </VCardText>
      </VCard>
    </div>
    
    <div v-else-if="!isLoading" class="text-center py-8">
      <VIcon
        icon="tabler-file-off"
        size="48"
        class="mb-4"
      />
      <div class="text-h6">
        {{ t('shipment.invoice_not_found') }}
      </div>
      <VBtn
        color="primary"
        class="mt-4"
        @click="goBack"
      >
        {{ t('shipment.go_back') }}
      </VBtn>
    </div>
  </div>
</template>

<style scoped>
/* Sheet-level page sizing — A4 with sane margins for both screen + PDF. */
.invoice-sheet {
  font-family: Arial, Helvetica, sans-serif;
  color: #111;
  font-size: 12px;
  line-height: 1.4;
}

/* Header: 3-column grid (company info | FACTURE+meta | reserved) */
.invoice-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  align-items: start;
  margin-bottom: 24px;
}

.invoice-header-left {
  font-size: 11px;
}

.invoice-logo-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.invoice-logo {
  width: 42px;
  height: auto;
}

.invoice-logo-text {
  font-weight: 700;
  letter-spacing: 0.18rem;
  line-height: 1.1;
  font-size: 13px;
}

.invoice-company {
  font-weight: 700;
  margin: 2px 0 4px;
  font-size: 13px;
}

.invoice-line {
  margin: 0 0 2px;
}

.invoice-header-center {
  text-align: left;
  padding-top: 4px;
}

.invoice-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.2rem;
  margin-bottom: 10px;
}

.invoice-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.invoice-meta-row {
  display: flex;
  gap: 6px;
}

.invoice-meta-label {
  min-width: 36px;
  font-weight: 400;
}

.invoice-meta-value {
  font-weight: 700;
}

.invoice-header-right {
  display: block;
}

/* Customer block (mimics 'ISMAEL' line in reference) */
.invoice-customer {
  margin: 16px 0 18px;
}

.invoice-customer-doit {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05rem;
  margin-bottom: 4px;
}

.invoice-customer-name {
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
}

.invoice-customer-phone {
  font-size: 11px;
  margin-top: 2px;
}

/* Compact fret table */
.fret-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-bottom: 18px;
}

.fret-table thead th {
  font-weight: 700;
  text-align: left;
  padding: 6px 4px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  background: #fff;
  text-transform: uppercase;
}

.fret-table tbody td {
  padding: 6px 4px;
  vertical-align: top;
}

.fret-table .total-row td {
  border-top: 1px solid #000;
  padding-top: 8px;
}

.fret-table .col-num { width: 4%; }
.fret-table .col-fret { width: 18%; }
.fret-table .col-type { width: 22%; }
.fret-table .col-unit { width: 8%; text-align: center; }
.fret-table .col-pu { width: 16%; text-align: right; }
.fret-table .col-qty { width: 14%; text-align: right; }
.fret-table .col-amount { width: 18%; text-align: right; }

/* Total in words */
.invoice-words {
  margin: 12px 0 18px;
  font-size: 11px;
}

/* Note block */
.invoice-note {
  margin: 18px 0 36px;
  font-size: 11px;
}

.invoice-note-row {
  display: flex;
  gap: 8px;
}

.invoice-note-label {
  min-width: 60px;
  font-weight: 400;
}

/* Per-parcel detail — compact secondary table */
.detail-section {
  margin: 24px 0 18px;
  page-break-before: auto;
}

.detail-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1rem;
  margin: 0 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #000;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
}

.detail-table thead th {
  font-weight: 700;
  text-align: left;
  padding: 4px 4px;
  border-bottom: 1px solid #000;
  text-transform: uppercase;
  font-size: 9.5px;
}

.detail-table tbody td {
  padding: 3px 4px;
  border-bottom: 1px dotted #ccc;
  vertical-align: top;
}

.detail-table .d-num { width: 4%; }
.detail-table .d-bag { width: 8%; }
.detail-table .d-tracking { width: 28%; word-break: break-all; }
.detail-table .d-dim { width: 22%; }
.detail-table .d-weight { width: 10%; text-align: right; }
.detail-table .d-volume { width: 12%; text-align: right; }
.detail-table .d-amount { width: 16%; text-align: right; }

#invoice-content.pdf-export .detail-table thead th,
#invoice-content.pdf-export .detail-table tbody td {
  font-size: 9.5px;
  padding: 3px 3px;
}

/* Signatures */
.invoice-signatures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 56px;
}

.invoice-signature-block {
  text-align: left;
}

.invoice-signature-label {
  font-weight: 700;
  text-decoration: underline;
  font-size: 12px;
}

#invoice-content {
  overflow: visible;
  margin: 0 auto;
  background: #fff;
}

/* Compact further when exporting to PDF so it fits on one A4 page */
#invoice-content.pdf-export {
  font-size: 10.5px;
}

#invoice-content.pdf-export .invoice-title {
  font-size: 20px;
}

#invoice-content.pdf-export .fret-table thead th,
#invoice-content.pdf-export .fret-table tbody td {
  padding: 5px 4px;
  font-size: 10.5px;
}

#invoice-content.pdf-export .v-card-text {
  padding: 16px;
}

@media (max-width: 960px) {
  .invoice-header {
    grid-template-columns: 1fr;
  }

  .invoice-header-center,
  .invoice-header-right {
    text-align: left;
    justify-content: flex-start;
  }

}
</style>
